import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { admin } from '../../api/client.js';
import { EmptyState } from '../../components/admin/AdminUI.jsx';

/**
 * What this organisation is spending on the assistant.
 *
 * Scoped server-side to the caller's own customer; there is no query string
 * here that could ask for another tenant's figures.
 *
 * Two presentation decisions worth knowing. Money is formatted at the point of
 * display from a number the API returned, never from a pre-formatted string,
 * so the same figures can be charted and summed. And calls the backend did not
 * report usage for are shown as a count rather than omitted - a total that
 * quietly excludes what it could not price is the kind of number that loses an
 * argument later.
 */
export default function AdminAiSpend() {
  const [months, setMonths] = useState(12);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'ai-usage', months],
    queryFn: () => admin.getAiUsage(months)
  });

  if (isLoading) return <div className="admin-page"><div className="spinner-border" /></div>;

  if (isError) {
    return (
      <div className="admin-page">
        <h1 className="h4 mb-3">Assistant spend</h1>
        <div className="alert alert-warning mb-0">
          Could not load usage. If this persists, the AI usage table may not have been
          created yet — check <code>/readyz</code> for <code>008_ai_usage.sql</code>.
        </div>
      </div>
    );
  }

  const rows = data?.months ?? [];
  const currency = data?.currency ?? 'USD';
  const current = rows[0] ?? null;
  const previous = rows[1] ?? null;
  const peak = Math.max(...rows.map(r => r.charge ?? 0), 0);
  const cap = data?.cap ?? null;

  return (
    <div className="admin-page">
      <div className="d-flex align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">Assistant spend</h1>
          <p className="text-secondary small mb-0">
            What your organisation has been charged for the assistant, at
            Vizora&rsquo;s current rates. Months run to UTC.
          </p>
        </div>
        <div className="ms-auto">
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
                  value={months} onChange={e => setMonths(Number(e.target.value))}>
            <option value={3}>Last 3 months</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
            <option value={24}>Last 24 months</option>
          </select>
        </div>
      </div>

      {/* Above the figures, not below them. If the assistant has stopped, that
          is the thing the administrator came here to find out - a banner under
          a year of history would be read last. */}
      {/* The cap's OWN currency, not the screen's. `data.currency` is derived
          from usage rows and is null for a customer with a cap but no usage
          yet, which fell back to USD and rendered a GBP cap as "$500.00" —
          while the email about the same cap said "£500.00". */}
      <CapPanel cap={cap} currency={cap?.currency ?? currency} months={months} />

      {rows.length === 0 ? (
        <EmptyState icon="bi-cash-coin" title="No usage recorded yet">
          Nothing has been recorded for this organisation. If the assistant is in use,
          the AI service may not yet be reporting usage — see
          <code className="ms-1">docs/10-AI-USAGE-CONTRACT.md</code>.
        </EmptyState>
      ) : (
        <>
          <div className="row g-3 mb-4">
            <Stat label="This month"
                  value={money(current?.charge, currency)}
                  sub={`${num(current?.calls)} conversations`} />
            <Stat label="Last month"
                  value={money(previous?.charge, currency)}
                  sub={changeLabel(current?.charge, previous?.charge)} />
            <Stat label="People using it"
                  value={num(current?.activeUsers)}
                  sub="this month" />
            <Stat label="Tokens"
                  value={compact(current?.totalTokens)}
                  sub={cacheLabel(current)} />
          </div>

          {current?.uncostedCalls > 0 && (
            <div className="alert alert-warning py-2 small">
              <i className="bi bi-exclamation-triangle me-1" />
              {num(current.uncostedCalls)} call{current.uncostedCalls === 1 ? '' : 's'} this
              month could not be priced, because the assistant used a model Vizora has no
              rate for. The figures above exclude them, so the real total is higher.
              Vizora has been notified.
            </div>
          )}

          {current?.unmarginedCalls > 0 && (
            <div className="alert alert-secondary py-2 small">
              {num(current.unmarginedCalls)} call{current.unmarginedCalls === 1 ? '' : 's'} this
              month {current.unmarginedCalls === 1 ? 'was' : 'were'} recorded before charging
              began and {current.unmarginedCalls === 1 ? 'is' : 'are'} shown at cost.
            </div>
          )}

          <h2 className="h6 text-secondary mb-2">By month</h2>
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Month</th>
                <th className="text-end">Conversations</th>
                <th className="text-end">Tokens</th>
                <th className="text-end">People</th>
                <th className="text-end">Charge</th>
                <th style={{ width: '28%' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.monthStartUtc}>
                  <td className="text-nowrap">{monthLabel(r.monthStartUtc)}</td>
                  <td className="text-end">{num(r.calls)}</td>
                  <td className="text-end text-secondary">{compact(r.totalTokens)}</td>
                  <td className="text-end text-secondary">{num(r.activeUsers)}</td>
                  <td className="text-end fw-medium">{money(r.charge, currency)}</td>
                  <td>
                    {/* A bar rather than a chart library: one dimension, one
                        comparison, and nothing to load. */}
                    <div className="bg-body-secondary rounded" style={{ height: 8 }}>
                      <div className="rounded" style={{
                        height: 8,
                        width: peak > 0 ? `${Math.round(((r.charge ?? 0) / peak) * 100)}%` : '0%',
                        background: 'var(--vz-magenta)'
                      }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="row g-4 mt-1">
            <div className="col-lg-6">
              <h2 className="h6 text-secondary mb-2">
                This month by model
              </h2>
              <Breakdown rows={data.byModel} currency={currency}
                         label={m => m.model} keyOf={m => m.model}
                         empty="No model detail for this month." />
            </div>
            <div className="col-lg-6">
              <h2 className="h6 text-secondary mb-2">This month by person</h2>
              <Breakdown rows={data.byUser} currency={currency}
                         label={u => u.email ?? '(deleted user)'} keyOf={u => u.email ?? 'unknown'}
                         empty="No per-person detail for this month." />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The monthly cap: where this organisation stands, and the two fields that
 * change it.
 *
 * Written as one panel rather than a banner plus a settings page because the
 * question "why has the assistant stopped" and the action "raise the cap" are
 * the same visit. Splitting them puts a navigation step between a person
 * finding the problem and fixing it.
 */
function CapPanel({ cap, currency, months }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  if (!cap) return null;

  /* The cap could not be read. Said plainly, because while this is true the
     assistant is refusing every question and reporting nothing here would
     leave an administrator hunting a fault in the AI service.

     Shown ALONGSIDE the form rather than instead of it. Returning early here
     removed the only control that can clear or lower the cap, at the one moment
     the assistant is refusing every question - locking an administrator out of
     the fix for the problem they are being told about. */
  const unreadable = cap.readable === false;

  const hasCap = cap.level !== 'none';
  const pct = hasCap && cap.fraction !== null
    ? Math.min(100, Math.round(cap.fraction * 100))
    : 0;

  const tone = cap.blocked ? 'danger' : cap.level === 'warning' ? 'warning' : 'secondary';

  return (
    <div className={`p-3 rounded mb-4 border border-${tone}${cap.level === 'ok' || !hasCap ? '-subtle' : ''}`}>
      <div className="d-flex align-items-start flex-wrap gap-2 mb-2">
        <div>
          <div className="fw-medium">Monthly cap</div>
          <div className="small text-secondary">
            {hasCap
              ? <>Spending stops for everyone when this month&rsquo;s charge reaches
                  the cap, and starts again on the 1st.</>
              : <>No cap is set. The assistant will keep working whatever it costs.</>}
          </div>
        </div>
        <div className="ms-auto">
          <button type="button" className="btn btn-sm btn-outline-secondary"
                  onClick={() => setEditing(e => !e)}>
            {editing ? 'Cancel' : hasCap ? 'Change' : 'Set a cap'}
          </button>
        </div>
      </div>

      {unreadable && (
        <div className="alert alert-danger py-2 small">
          <i className="bi bi-exclamation-octagon me-1" />
          Your spend against the monthly cap could not be read, so the assistant is
          refusing questions as a precaution. This is usually temporary. If it
          persists, contact Vizora — or lower or clear the cap below to release it.
        </div>
      )}

      {hasCap && !unreadable && (
        <>
          {cap.blocked && (
            <div className="alert alert-danger py-2 small mb-2">
              <i className="bi bi-exclamation-octagon me-1" />
              <strong>The assistant is paused for everyone in your organisation.</strong>{' '}
              {cap.source === 'ceiling'
                ? <>This is the limit set by Vizora, so it cannot be raised here —
                    contact Vizora.</>
                : <>Raise the cap below to start it again immediately.</>}{' '}
              Reports and dashboards are unaffected.
            </div>
          )}

          {!cap.blocked && cap.level === 'warning' && (
            <div className="alert alert-warning py-2 small mb-2">
              Over {cap.warnAtPercent}% of the cap has been used. The assistant stops
              for everyone when it is reached.
            </div>
          )}

          <div className="d-flex justify-content-between small mb-1">
            <span>{money(cap.spentAmount, currency)} used</span>
            <span className="text-secondary">
              {money(cap.capAmount, currency)}
              {cap.source === 'ceiling' && ' (set by Vizora)'}
            </span>
          </div>
          <div className="bg-body-secondary rounded" style={{ height: 10 }}>
            <div className="rounded" style={{
              height: 10, width: `${pct}%`,
              background: cap.blocked
                ? 'var(--bs-danger)'
                : cap.level === 'warning' ? 'var(--bs-warning)' : 'var(--vz-magenta)'
            }} />
          </div>
          <div className="small text-secondary mt-1">
            {cap.blocked
              ? 'Nothing left this month.'
              : `${money(cap.remaining, currency)} left this month.`}
            {cap.ceilingAmount !== null && cap.source !== 'ceiling'
              && ` Vizora’s limit is ${money(cap.ceilingAmount, currency)}.`}
          </div>

          {/* Two ways the cap can be a worse control than it appears, both
              stated rather than left to be discovered. */}
          {cap.uncostedCalls > 0 && (
            <div className="alert alert-warning py-2 small mt-2 mb-0">
              {num(cap.uncostedCalls)} call{cap.uncostedCalls === 1 ? '' : 's'} this month
              could not be priced and {cap.uncostedCalls === 1 ? 'is' : 'are'} therefore
              not counted against the cap. Real spend is higher than the figure above.
            </div>
          )}
          {cap.mixedCurrency && (
            <div className="alert alert-warning py-2 small mt-2 mb-0">
              This month&rsquo;s usage is recorded in more than one currency, so the
              total — and the cap — are not reliable. Vizora has been notified.
            </div>
          )}
          {!cap.notifyEmail && (
            <div className="small text-secondary mt-2">
              <i className="bi bi-envelope-slash me-1" />
              No notification address is set, so nobody will be emailed before the
              assistant stops.
            </div>
          )}
          {cap.notifyEmail && !cap.notificationsPossible && (
            <div className="alert alert-warning py-2 small mt-2 mb-0">
              Email is not configured on this portal, so the notices to{' '}
              <code>{cap.notifyEmail}</code> will not be sent. The cap is still enforced.
            </div>
          )}
        </>
      )}

      {editing && (
        <CapForm cap={cap} currency={currency}
                 onDone={() => {
                   setEditing(false);
                   queryClient.invalidateQueries({ queryKey: ['admin', 'ai-usage', months] });
                 }} />
      )}
    </div>
  );
}

function CapForm({ cap, currency, onDone }) {
  /* Held as a string, not a number. An empty box means "no cap" and a typed 0
     means "no spending at all" - two opposite instructions that a numeric state
     initialised to 0 would make indistinguishable. */
  const [amount, setAmount] = useState(
    cap.customerAmount === null || cap.customerAmount === undefined
      ? '' : String(cap.customerAmount));
  const [email, setEmail] = useState(cap.notifyEmail ?? '');
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => admin.saveAiCap({
      amount: amount.trim() === '' ? null : amount.trim(),
      notifyEmail: email.trim() === '' ? null : email.trim()
    }),
    onSuccess: onDone,
    /* issues FIRST. The error handler returns { error: 'validation_failed',
       issues: [...] } for a rejected body, so reading `error` alone rendered
       the literal string "validation_failed" - discarding the whole point of
       the server taking trouble over the wording. Customers.jsx already did
       this correctly; this one did not. */
    onError: (err) => setError(
      err.body?.issues?.map(i => i.message).join('; ')
      ?? err.body?.error ?? err.message)
  });

  return (
    <form className="border-top mt-3 pt-3" onSubmit={e => {
      e.preventDefault(); setError(null); save.mutate();
    }}>
      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label small" htmlFor="cap-amount">
            Monthly cap ({currency})
          </label>
          <input id="cap-amount" className="form-control form-control-sm"
                 inputMode="decimal" value={amount} placeholder="No cap"
                 onChange={e => setAmount(e.target.value)} />
          <div className="form-text">
            Leave empty for no cap. Enter 0 to stop all assistant spending.
            {cap.ceilingAmount !== null
              && ` Cannot be above ${money(cap.ceilingAmount, currency)}, set by Vizora.`}
          </div>
        </div>
        <div className="col-md-5">
          <label className="form-label small" htmlFor="cap-email">
            Send notices to
          </label>
          <input id="cap-email" className="form-control form-control-sm" type="email"
                 value={email} placeholder="billing@example.com"
                 onChange={e => setEmail(e.target.value)} />
          <div className="form-text">
            One address — a shared billing or IT mailbox works well. Emailed at{' '}
            {cap.warnAtPercent}% of the cap, and again when it is reached.
          </div>
        </div>
        <div className="col-md-3 d-flex align-items-start">
          <button type="submit" className="btn btn-sm btn-primary mt-4"
                  disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {error && <p className="text-danger small mt-2 mb-0">{error}</p>}
    </form>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="col-6 col-lg-3">
      <div className="p-3 rounded bg-body-secondary h-100">
        <div className="small text-secondary">{label}</div>
        <div className="fs-4 fw-medium">{value}</div>
        <div className="small text-secondary">{sub}</div>
      </div>
    </div>
  );
}

function Breakdown({ rows = [], currency, label, keyOf, empty }) {
  if (rows.length === 0) return <p className="text-secondary small">{empty}</p>;
  return (
    <table className="table table-sm">
      <tbody>
        {rows.map(r => (
          <tr key={keyOf(r)}>
            <td className="text-truncate" style={{ maxWidth: 220 }}>{label(r)}</td>
            <td className="text-end text-secondary small">{num(r.calls)}</td>
            <td className="text-end fw-medium">{money(r.charge, currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* --------------------------- formatting helpers ------------------------- */

/* Four decimal places, not two. A month of light use genuinely can come to
   less than a penny, and showing "$0.00" next to real usage reads as broken
   rather than as cheap.

   Every figure on this screen is the charge - cost plus margin. The underlying
   cost is not in the payload at all, so there is nothing here to leak. */
function money(value, currency) {
  if (value === null || value === undefined) return '—';
  const digits = value > 0 && value < 0.01 ? 4 : 2;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: currency ?? 'USD',
    minimumFractionDigits: digits, maximumFractionDigits: digits
  }).format(value);
}

function num(value) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB').format(value);
}

function compact(value) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 })
    .format(value);
}

function monthLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function changeLabel(current, previous) {
  if (!previous || current === null || current === undefined) return 'no comparison';
  const delta = ((current - previous) / previous) * 100;
  if (!Number.isFinite(delta)) return 'no comparison';
  const rounded = Math.round(delta);
  if (rounded === 0) return 'about the same';
  return `${rounded > 0 ? '+' : ''}${rounded}% this month`;
}

/* Cache hit rate, because it is the single biggest lever on this bill and
   nobody would think to ask for it. The assistant resends the whole
   conversation every turn, so a low rate on long threads is money. */
function cacheLabel(month) {
  if (!month?.inputTokens) return 'this month';
  const rate = Math.round((month.cachedInputTokens / month.inputTokens) * 100);
  return `${rate}% of prompt tokens cached`;
}
